import {
  Button,
  DatePicker,
  Form,
  Icon,
  Input,
  InputNumber,
  message,
  Select,
} from 'antd';
import { EventSubscription } from 'fbemitter';
import * as moment from 'moment';
import * as React from 'react';
import { newScheduleRequest } from '../../PoolAPI';
import EditorStore from '../../stores/EditorStore';
import { removeListener } from '../../utils';

interface PoolRequestTabState {
  requestRange: [moment.Moment, moment.Moment];
  numOffsiteDays: number;
  offsiteDaysUnit: 'week' | 'month';
  lastSignupDay: moment.Moment | null;
  customMessage: string;
  reminderTimes: (moment.Moment | null)[];

  editorVolumes: number[];
  islandVolumes: number[];
}

export class PoolRequestTab extends React.Component<{}, PoolRequestTabState> {
  private editorListener: EventSubscription | null = null;

  constructor(props: {}) {
    super(props);

    const startOfMonth = moment().add(1, 'month').startOf('month');
    const endOfMonth = moment().add(1, 'month').endOf('month');
    this.state = {
      requestRange: [startOfMonth, endOfMonth],
      numOffsiteDays: 2,
      offsiteDaysUnit: 'month',
      lastSignupDay: moment().add(2, 'day'),
      customMessage: '',
      reminderTimes: [],

      editorVolumes: EditorStore.getVolumes(),
      islandVolumes: [],
    };

    this.submit = this.submit.bind(this);
  }

  render() {
    const volumeOptions = this.state.editorVolumes.map((v: number) => {
      return <Select.Option key={v}>{v}</Select.Option>;
    });

    return (
      <div>
        <h2>Month to request for:</h2>
        <div className="buffer">
          <DatePicker.RangePicker
            value={this.state.requestRange}
            onChange={(r: [moment.Moment, moment.Moment]) =>
              this.rangeChanged(r)
            }
          />
        </div>
        <h2>Number of allowed offsite days:</h2>
        <div className="buffer">
          <Form layout="inline">
            <Form.Item label="# of off-site days">
              <InputNumber
                min={0}
                value={this.state.numOffsiteDays}
                onChange={(input: number | string) => {
                  let days = parseInt('' + input, 10);
                  if (isNaN(days) || days < 0) {
                    days = 0;
                  }
                  this.setState({
                    numOffsiteDays: days,
                  });
                }}
              />
            </Form.Item>
            <Form.Item>
              <Select
                defaultValue="month"
                onChange={(u: 'week' | 'month') => this.selectUnits(u)}
              >
                <Select.Option value="month">per month</Select.Option>
                <Select.Option value="week">per week</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </div>
        <h2>Allow editors to modify their selection until 2pm EST on:</h2>
        <div className="buffer">
          <DatePicker
            value={
              this.state.lastSignupDay === null
                ? undefined
                : this.state.lastSignupDay
            }
            onChange={(d: moment.Moment | null) => this.signupDayChanged(d)}
          />
        </div>
        <h2>Allow Editors in the following Volumes to take island days:</h2>
        <div className="buffer">
          <Select
            mode="multiple"
            placeholder="Please select"
            style={{ width: 300 }}
            value={this.state.islandVolumes.map((v: number) => '' + v)}
            onChange={(v: string[]) => {
              this.setState({
                islandVolumes: v.map((s: string) => parseInt(s, 10)),
              });
            }}
          >
            {volumeOptions}
          </Select>
        </div>
        <h2>Reminder emails (optional):</h2>
        <p>
          We will automatically send an email as soon as this form is submitted.
          You have the option to schedule the timing of as many additional
          emails as you want.
        </p>
        {this.state.reminderTimes.map(
          (m: moment.Moment | null, index: number) => {
            return (
              <Form.Item key={'reminder-date-picker-' + index}>
                <DatePicker
                  style={{ width: 200 }}
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm"
                  value={m === null ? undefined : m}
                  onChange={(d: moment.Moment | null) => {
                    const newTimes = this.state.reminderTimes.slice(); // make a copy
                    newTimes[index] = d;
                    this.setState({
                      reminderTimes: newTimes,
                    });
                  }}
                />
                &nbsp;
                <Icon
                  className="dynamic-delete-button"
                  type="minus-circle-o"
                  onClick={() => {
                    const newTimes = this.state.reminderTimes.slice();
                    newTimes.splice(index, 1);
                    this.setState({
                      reminderTimes: newTimes,
                    });
                  }}
                />
              </Form.Item>
            );
          }
        )}
        <Form.Item>
          <Button
            style={{ width: 200 }}
            type="dashed"
            onClick={() => {
              const newTimes = this.state.reminderTimes;
              newTimes.push(null);
              this.setState({
                reminderTimes: newTimes,
              });
            }}
          >
            <Icon type="plus" /> Add reminder email
          </Button>
        </Form.Item>
        <h2>Custom message (optional):</h2>
        <div className="buffer">
          <Input.TextArea
            value={this.state.customMessage}
            placeholder="The message you put here will be added to the email that is sent."
            autosize={{ minRows: 2 }}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              this.customMessageChanged(e)
            }
          />
        </div>
        <p>&nbsp;{this.errorStrings().join(' ')}</p>
        <Button
          type="primary"
          disabled={!this.canSubmit()}
          onClick={this.submit}
        >
          Send Email to Editors
        </Button>
      </div>
    );
  }

  componentDidMount() {
    this.editorListener = EditorStore.addListener(() => {
      this.setState({
        editorVolumes: EditorStore.getVolumes(),
      });
    });
  }

  componentWillUnmount() {
    removeListener(this.editorListener);
  }

  private canSubmit(): boolean {
    return this.errorStrings().length === 0;
  }

  private async submit() {
    if (!this.canSubmit()) {
      return;
    }

    const r = await newScheduleRequest({
      term: this.state.requestRange[0].format('YYYY-MM'),
      startDay: this.state.requestRange[0].date(),
      endDay: this.state.requestRange[1].date(),
      offsiteDays: this.state.numOffsiteDays,
      offsiteUnit: this.state.offsiteDaysUnit,
      submissionDeadline: (this.state.lastSignupDay as moment.Moment).format(
        'YYYY-MM-DD'
      ),
      reminderEmails: this.state.reminderTimes.map(
        (v: moment.Moment | null) => {
          if (v === null) {
            return '';
          }
          v.utcOffset(0);
          return v.format('YYYY-MM-DDTHH:mm');
        }
      ),
      islandVolumes: this.state.islandVolumes,
      customMessage: this.state.customMessage,
    });

    if (r.status === 200) {
      message.success('Emails sent!');
    } else if (r.status >= 400) {
      message.error('Email send failed.');
    }
  }

  private errorStrings(): string[] {
    const errorStrings: string[] = [];
    const rangeStart = this.state.requestRange[0];
    const rangeEnd = this.state.requestRange[1];
    const now = moment();
    if (rangeStart.month() !== rangeEnd.month()) {
      errorStrings.push(
        'Requested range must start and end in the same month.'
      );
    }
    if (this.state.lastSignupDay === null) {
      errorStrings.push(
        'You must pick an end date by which all pool days must be submitted.'
      );
    } else if (this.state.lastSignupDay.isSameOrBefore(now)) {
      errorStrings.push('Last signup day must be after today.');
    }
    for (let i = 0; i < this.state.reminderTimes.length; i++) {
      const time = this.state.reminderTimes[i];
      if (time === null) {
        errorStrings.push(
          'One of the reminder times has not yet been assigned.'
        );
        break;
      } else if (
        this.state.lastSignupDay !== null &&
        !time.isSameOrBefore(this.state.lastSignupDay)
      ) {
        errorStrings.push(
          'The reminder emails must come before the last day that submitting is allowed.'
        );
        break;
      } else if (time.isSameOrBefore(now)) {
        errorStrings.push(
          'All reminders must be scheduled for some time in the future.'
        );
        break;
      }
    }

    return errorStrings;
  }

  private rangeChanged(dates: [moment.Moment, moment.Moment]) {
    this.setState({
      requestRange: dates,
    });
  }

  private customMessageChanged(e: React.ChangeEvent<HTMLTextAreaElement>) {
    this.setState({
      customMessage: e.target.value,
    });
  }

  private selectUnits(units: 'week' | 'month') {
    this.setState({
      offsiteDaysUnit: units,
    });
  }

  private signupDayChanged(date: moment.Moment | null) {
    this.setState({
      lastSignupDay: date,
    });
  }
}



// WEBPACK FOOTER //
// ./src/components/AdminTabs/PoolRequestTab.tsx